import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Typography, Card  } from "@material-tailwind/react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

import Loader from "../../../../components/Loader";
import { RootState } from "../../../../redux";
import { Vehicle, updateVehicleRequest, uploadMediaRequest, genBookletRequest, getBookletCarfaxRequest, genBookletFactoryBaseConfigurationRequest, genBookletWhatWeDidRequest, genBookletNotesRequest, genBookletWhatHappenedRequest } from "../../../../redux/vehicles/actions";
import { RenderFormField } from "../../../../components/DetailsFormField";
import FormCheckbox from "../../../../components/FormCheckbox";
import { FormProvider, useForm } from "react-hook-form";
import { Section } from "../New";
import AppImages from "../../../../assets/AppImages";
import FormFileUpload from "../../../../components/FormFileUpload";
import FileUpload from "../../../../components/FileUpload";
import { Checklist } from "../../../../redux/auth/actions";
import { MediaType, Media, BeforeAfterMedia } from "../../../../redux/vehicles/actions";
import { Upload } from "iconoir-react";
import { DialogBeforeAfterUpload } from "../../../../components/DialogBeforeAfterUpload";

const MEDIA_SECTIONS = [
    {type: "booklet", name: "Attachments", id: 1, views: [] as MediaType[]},
];

const defaultRequiredValue = true;

export const SECTIONS: Section[] = [
    {
        title: "Inspection Details",
        subTitle: "Here's a quick overview of its standout features:",
        type: "render",
        fields: [
            { n: "Inspected Date", f: "booklet_inspected_date", type: "date", editable: true, required: defaultRequiredValue },
            { n: "Inspected By", f: "booklet_inspected_by", type: "text", editable: true, required: defaultRequiredValue },
            { n: "Notes", f: "booklet_notes", type: "textarea", editable: true, required: false, colspan: 3 },
            { n: "Summary", f: "booklet_summary", type: "textarea", editable: true, required: false, colspan: 3 },
        ]
    },
    {
        title: "Inspection Checklist",
        subTitle: "",
        type: "checklist",
    },
    {
        title: "Attachments",
        subTitle: "",
        type: "attachments",
    },
    {
        title: "Carfax",
        subTitle: "",
        type: "render",
        fields: [
            { n: "Carfax status", f: "booklet_carfax_overview_title", type: "text", editable: true, required: false, colspan: 3 },
            { n: "Carfax overview", f: "booklet_carfax_overview", type: "textarea", editable: true, required: false, colspan: 3 },
            { n: "Carfax report json", f: "booklet_carfax_report_json", type: "textarea", editable: false, required: false, colspan: 3 },
            { n: "Carfax report file", f: "booklet_carfax_report", type: "file", editable: true, required: false, accept:".jpg,.png,.jpeg,.pdf"},
        ]
    },
    {
        title: "What happened",
        subTitle: "",
        type: "render",
        fields: [
            { n: "Overview", f: "booklet_what_happened", type: "textarea", editable: true, required: false, colspan: 3 },
        ]
    },
    {
        title: "What we did",
        subTitle: "",
        type: "render",
        fields: [
            { n: "Labor", f: "booklet_what_we_did_labor", type: "textarea", editable: true, required: false, colspan: 3 },
            { n: "Parts", f: "booklet_what_we_did_parts", type: "textarea", editable: true, required: false, colspan: 3 },
            { n: "Overview", f: "booklet_what_we_did_overview", type: "textarea", editable: true, required: false, colspan: 3 },
        ]
    },
    {
        title: "",
        subTitle: "",
        type: "before_after",
    },
    {
        title: "Factory Base Configuration",
        subTitle: "",
        type: "render",
        fields: [
            { n: "Overview", f: "booklet_factory_base_configuration_overview", type: "textarea", editable: true, required: false, colspan: 3 },
            { n: "MSRP ($)", f: "booklet_factory_base_configuration_msrp", type: "money", editable: true, required: false },
            { n: "File", f: "booklet_factory_base_configuration_file", type: "file", editable: true, required: false, accept:".jpg,.png,.jpeg,.pdf" },
        ]
    }
]

export const renderFeatures = (booklet_inspection: Checklist[], isEdit: boolean) => {
    return (
        <div className={`mt-6 grid grid-cols-1 md:grid-cols-3 gap-4`}>
            {booklet_inspection?.map((item, sectionIndex) => (
                <div key={sectionIndex}>
                    <h2 className="mb-3">{item.name}</h2>
                    <div className="flex flex-col gap-3">
                        {item.items.map((item, index) => (
                            <React.Fragment key={index}>
                                <FormCheckbox
                                    name="booklet_inspection_checklist"
                                    label={item.name}
                                    value={item.id}
                                    readonly={!isEdit}
                                // textClassName="text-[16px] text-[#1654EC] font-[450]"
                                />
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}


export function DashboardVehicleInventoryTabBooklet() {
    const dispatch = useDispatch();
    const methods = useForm();

    const { vehicle, mediatypes } = useSelector((state: RootState) => state.vehicles);
    const { booklet_checklist } = useSelector((state: RootState) => state.auth);

    const [isEdit, setIsEdit] = useState(false);
    const [sections, setSections] = useState(MEDIA_SECTIONS);
    const [newVehicle, setNewVehicle] = useState<Vehicle>({} as Vehicle);
    const [sectionsMedias, setSectionsMedias] = useState<Record<string, Media[]>>({});
    const [beforeAfterMedias, setBeforeAfterMedias] = useState<BeforeAfterMedia[]>([]);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const updatedVehicle = useRef<Vehicle>({} as Vehicle);
    const loadingFiles = useRef<number>(0);
    const loadedFiles = useRef<number>(0);

    useEffect(() => {
        if (vehicle) {
            setNewVehicle(vehicle);
            const medias: Record<number, Media[]> = {};

            vehicle?.medias?.forEach((media: Media) => {
                if (!medias[media.section.id]) {
                    medias[media.section.id] = [];
                }
                medias[media.section.id].push(media);
            });

            setSectionsMedias(medias);

            const beforeAfterMedias: BeforeAfterMedia[] = [];
            vehicle?.before_after?.forEach((media: BeforeAfterMedia) => {
                beforeAfterMedias.push(media);
            });
            setBeforeAfterMedias(beforeAfterMedias);
        }
    }, [vehicle]);

   useEffect(() => {
       const subscription = methods.watch((value, { name }) => {
           if (!name || !value[name]) return;

           console.log("form", value);
           console.log(`Form field "${name}" changed to`, value[name]);
       });
       return () => subscription.unsubscribe();
   }, [methods]);

    useEffect(() => {
        if (mediatypes.length > 0) {
            MEDIA_SECTIONS.forEach((section) => {
                const mediaType = mediatypes.find((mt) => mt.name === section.name);
                if (mediaType) {
                    section.id = mediaType.id;
                }

                if (section.name === "Attachments") {
                    section.views = mediatypes.filter((mt) => mt.is_booklet);
                }
            });
            setSections(MEDIA_SECTIONS);
        }
    }, [mediatypes]);


    useEffect(() => {
        if (vehicle) {
            // @ts-ignore
            const v = {...vehicle, booklet_what_we_did_overview: JSON.stringify(vehicle.booklet_what_we_did_overview)} as Vehicle;
            methods.reset(v);
            setNewVehicle(v);
        }
    }, [vehicle, methods]);

    const checkLoadingFiles = () => {
        console.log("loadingFiles", loadingFiles.current);
        console.log("loadedFiles", loadedFiles.current);
        console.log("updatedVehicle", JSON.stringify(updatedVehicle.current));

        loadedFiles.current++;

        if (loadedFiles.current === loadingFiles.current) {
            dispatch( updateVehicleRequest({
                vehicle: updatedVehicle.current,
                update_fields: Object.keys(updatedVehicle.current)
            }) );
        }
    }

    const onFormSubmit = methods.handleSubmit(d => {
        const data = {...d, booklet_what_we_did_overview: JSON.parse(d.booklet_what_we_did_overview)} as Vehicle;
        console.log("onFormSubmit", data);
        setIsEdit(false);

        const all_fields = [
            ...SECTIONS.flatMap(section => section.fields?.map(field => field?.f)),
            "booklet_inspection_checklist",
        ];

        const update_fields = all_fields.filter(
            (field) => field !== undefined && vehicle !== null && field in vehicle && data[field] !== vehicle?.[field as keyof Vehicle]
        ) || [];

        if (update_fields.length === 0) return;

        let lf = 1;

        if (update_fields.indexOf('booklet_factory_base_configuration_file') !== -1) {
            lf++;
        }
        if (update_fields.indexOf('booklet_carfax_report') !== -1) {
            lf++;
        }

        updatedVehicle.current = { 'id': vehicle?.id, ...Object.fromEntries(Object.entries(data).filter(([key]) => update_fields.includes(key)))} as Vehicle;
        loadingFiles.current = lf;
        loadedFiles.current = 0;

        if (update_fields.indexOf('booklet_carfax_report') !== -1) {
            const reader = new FileReader();
            reader.onloadend = () => {
                updatedVehicle.current = {
                    ...updatedVehicle.current,
                    booklet_carfax_report: reader.result,
                } as Vehicle;
                checkLoadingFiles();
            };
            // @ts-ignore
            if (data.booklet_carfax_report && data.booklet_carfax_report[0] instanceof Blob) {
                reader.readAsDataURL(data.booklet_carfax_report[0]);
            }
        }
        if (update_fields.indexOf('booklet_factory_base_configuration_file') !== -1) {
            const reader = new FileReader();
            reader.onloadend = () => {
                updatedVehicle.current = {
                    ...updatedVehicle.current,
                    booklet_factory_base_configuration_file: reader.result,
                } as Vehicle;
                checkLoadingFiles();
            };
            // @ts-ignore
            if (data.booklet_factory_base_configuration_file && data.booklet_factory_base_configuration_file[0] instanceof Blob) {
                reader.readAsDataURL(data.booklet_factory_base_configuration_file[0]);
            }
        }

        checkLoadingFiles();
    });

    const renderAttachments = () => {
        return (
            <React.Fragment key={Math.random()}>
                {sections
                    .filter((section) => section.views.length > 0)
                    .map((section, index) => (
                        <div
                            key={`${index}-${section.views.length}`}
                            className="flex flex-col gap-5"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-5 justify-stretch">
                                {section.views.map((item, index) => (
                                    <div
                                        key={index}
                                        className="flex border min-h-[250px] border-gray-200 rounded-md flex-col items-center justify-center gap-y-3"
                                    >
                                        <FileUpload
                                            name={`img_${item.id}`}
                                            accept=".jpg,.png,.jpeg"
                                            title={item.name}
                                            gap="gap-y-3"
                                            iconClass={item.icon ? "!w-8 !h-8" : "!w-5 !h-5"}
                                            btnClass="!py-1 !px-2"
                                            Icon={item.icon}
                                            desc=""
                                            media={sectionsMedias[item.id]?.[0] ?? null}
                                            onFileChange={(files) => {
                                                if (sectionsMedias[item.id]?.[0]) {
                                                    dispatch(
                                                        updateVehicleRequest({
                                                            vehicle: {
                                                                id: vehicle?.id || 0,
                                                                removed_medias: [
                                                                    sectionsMedias[item.id]?.[0]
                                                                        ?.id,
                                                                ],
                                                            } as unknown as Vehicle,
                                                            update_fields: ["id", "removed_medias"],
                                                        })
                                                    );
                                                }
                                                dispatch(
                                                    uploadMediaRequest({
                                                        vehicle_id: vehicle?.id || 0,
                                                        files: files,
                                                        section_id: item.id,
                                                    })
                                                );
                                            }}
                                            onFileRemove={(id) => {
                                                withReactContent(Swal)
                                                    .fire({
                                                        title: "Are you sure?",
                                                        text: "You won't be able to revert this!",
                                                        icon: "warning",
                                                        showCancelButton: true,
                                                        confirmButtonColor: "#3085d6",
                                                        cancelButtonColor: "#d33",
                                                        confirmButtonText: "Yes, delete it!",
                                                    })
                                                    .then((result) => {
                                                        if (result.isConfirmed) {
                                                            dispatch(
                                                                updateVehicleRequest({
                                                                    vehicle: {
                                                                        id: vehicle?.id || 0,
                                                                        removed_medias: [id],
                                                                    } as unknown as Vehicle,
                                                                    update_fields: [
                                                                        "id",
                                                                        "removed_medias",
                                                                    ],
                                                                })
                                                            );
                                                        }
                                                    })
                                                    .catch((error: any) => {
                                                        console.log("error", error);
                                                    });
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
            </React.Fragment>
        );
    }

    const renderBeforeAfter = () => {
        return (
            <React.Fragment key={Math.random()}>
                <Card.Header className="flex justify-between">
                    <div>
                        <Typography className="block font-bold text-primary">
                            Before and After
                        </Typography>
                        <Typography type="small" className="block text-foreground">
                            Upload vehicle images for before and after.
                        </Typography>
                    </div>
                    <div className="flex items-center gap-x-3">
                        <Button
                            size="sm"
                            variant="outline"
                            color="secondary"
                            onClick={() => setUploadDialogOpen(true)}
                            className="h-8 flex items-center gap-2 sm:w-auto w-full"
                        >
                            Upload
                            <Upload className="w-5 h-5" />
                        </Button>
                    </div>
                </Card.Header>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 justify-stretch">
                    {beforeAfterMedias
                        .map((item, index) => (
                            <div
                                key={index}
                                className="flex rounded-md items-center justify-top gap-y-3 relative group overflow-hidden border border-[#3085d6] hover:border-[#1654EC] transition-all duration-200"
                            >
                                <button
                                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
                                    onClick={() => {
                                        withReactContent(Swal)
                                            .fire({
                                                title: "Are you sure?",
                                                text: "You won't be able to revert this!",
                                                icon: "warning",
                                                showCancelButton: true,
                                                confirmButtonColor: "#3085d6",
                                                cancelButtonColor: "#d33",
                                                confirmButtonText: "Yes, delete it!",
                                            })
                                            .then((result) => {
                                                if (result.isConfirmed) {
                                                    dispatch(
                                                        updateVehicleRequest({
                                                            vehicle: {
                                                                id: vehicle?.id || 0,
                                                                removed_before_after: [item.id],
                                                            } as unknown as Vehicle,
                                                            update_fields: ["id", "removed_before_after"],
                                                        })
                                                    );
                                                }
                                            }).catch((error: any) => {
                                                console.log("error", error);
                                            });
                                    }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                                <div className="w-1/2 relative">
                                    <img src={item.before} alt="Before" className="w-full h-full object-cover" />
                                    <Typography className="absolute bg-white rounded-md w-max top-1 left-1/2 -translate-x-1/2 px-2 text-center text-sm text-foreground">
                                        Before
                                    </Typography>
                                </div>
                                <div className="w-1/2 relative">
                                    <img src={item.after} alt="After" className="w-full h-full object-cover" />
                                    <Typography className="absolute bg-white rounded-md w-max top-1 left-1/2 -translate-x-1/2 px-2 text-center text-sm text-foreground">
                                        After
                                    </Typography>
                                </div>
                            </div>
                        ))}
                </div>
                {uploadDialogOpen && (
                    <DialogBeforeAfterUpload
                        isOpen={uploadDialogOpen}
                        setIsOpen={setUploadDialogOpen}
                        vehicleId={vehicle?.id || 0}
                        sectionId={sections[0].id}
                    />
                )}
            </React.Fragment>
        );
    }

   return !newVehicle?.id ? (
       <div className="flex justify-center items-center h-full">
           <Loader />
       </div>
   ) : (
       <React.Fragment>
           <FormProvider {...methods}>
               <form
                   className="flex flex-col mt-4"
                   onSubmit={(e) => e.preventDefault()}
                   noValidate
                   autoComplete="off"
               >
                   {SECTIONS.map((section, index) => (
                       <div key={index}>
                           <Card className={`p-4 mb-4`}>
                               <div className="flex justify-between">
                                   {section.title || section.subTitle ? (
                                       <div>
                                           <Typography className="text-[16px] text-[#1F2937] font-bold">
                                               {section.title}
                                           </Typography>
                                           <Typography className="mt-2 text-[14px] text-[#64748B] font-[450]">
                                               {section.subTitle}
                                           </Typography>
                                       </div>
                                   ) : null}
                                   {index === 0 ? (
                                       <Button
                                           variant="outline"
                                           color="info"
                                           className="h-max"
                                           onClick={() => {
                                               if (isEdit) {
                                                   onFormSubmit();
                                               } else {
                                                   setIsEdit(!isEdit);
                                               }
                                           }}
                                       >
                                           {isEdit ? "Save" : "Edit"}
                                       </Button>
                                   ) : null}
                                   {index === 1 && isEdit ? (
                                       <div className="flex items-center gap-x-3">
                                           <Button
                                               variant="outline"
                                               color="info"
                                               className="h-max"
                                               onClick={() => {
                                                   methods.setValue("booklet_inspection_checklist", booklet_checklist?.flatMap((section) => section.items.map((item) => item.id.toString())));
                                               }}
                                           >
                                               Select All
                                           </Button>
                                           <Button
                                               variant="outline"
                                               color="info"
                                               className="h-max"
                                               onClick={() => {
                                                   methods.setValue("booklet_inspection_checklist", []);
                                               }}
                                           >
                                               Deselect All
                                           </Button>
                                       </div>
                                   ) : null}
                               </div>

                               {section.type === "render" ? (
                                   <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                                       {section.fields
                                           ?.filter((field) => !field.hide)
                                           .map((field, fieldIndex) => (
                                               <div key={fieldIndex} className={`col-span-${field.colspan || 1}`}>
                                                   <RenderFormField
                                                       field={field}
                                                       isEdit={isEdit}
                                                       object={newVehicle}
                                                       setNewObject={setNewVehicle}
                                                   />
                                               </div>
                                           ))}
                                   </div>
                               ) : section.type === "checklist" ? (
                                   renderFeatures(booklet_checklist || [], isEdit)
                               ) : section.type === "attachments" ? (
                                   renderAttachments()
                               ) : section.type === "before_after" ? (
                                   renderBeforeAfter()
                               ) : null}

                               {!isEdit && index === 0 ? (
                                   <div className="mt-4 flex gap-x-3">
                                       <Button
                                           variant="outline"
                                           color="info"
                                           className="h-max"
                                           onClick={() => {
                                               withReactContent(Swal)
                                                   .fire({
                                                       title: "Generate Summary",
                                                       text: "Are you sure you want to generate summary?",
                                                       icon: "warning",
                                                       showCancelButton: true,
                                                       confirmButtonColor: "#3085d6",
                                                   })
                                                   .then((result) => {
                                                       if (result.isConfirmed) {
                                                           dispatch(
                                                               genBookletRequest({
                                                                   vehicle_id: vehicle?.id || 0,
                                                               })
                                                           );
                                                       }
                                                   })
                                                   .catch((error: any) => {
                                                       console.log("error", error);
                                                   });
                                           }}
                                       >
                                           Generate Summary
                                       </Button>
                                       <Button
                                           variant="outline"
                                           color="info"
                                           className="h-max"
                                           onClick={() => {
                                               withReactContent(Swal)
                                                   .fire({
                                                       title: "Generate Notes",
                                                       text: "Are you sure you want to generate notes?",
                                                       icon: "warning",
                                                       showCancelButton: true,
                                                       confirmButtonColor: "#3085d6",
                                                   })
                                                   .then((result) => {
                                                       if (result.isConfirmed) {
                                                           dispatch(
                                                               genBookletNotesRequest({
                                                                   vehicle_id: vehicle?.id || 0,
                                                               })
                                                           );
                                                       }
                                                   })
                                                   .catch((error: any) => {
                                                       console.log("error", error);
                                                   });
                                           }}
                                       >
                                           Generate Notes
                                       </Button>
                                   </div>
                               ) : null}

                               {!isEdit && index === 3 ? (
                                   <div className="mt-4">
                                       <Button
                                           variant="outline"
                                           color="info"
                                           className="h-max"
                                           onClick={() => {
                                               withReactContent(Swal)
                                                   .fire({
                                                       title: "Generate Carfax",
                                                       text: "Are you sure you want to generate Carfax?",
                                                       icon: "warning",
                                                       showCancelButton: true,
                                                       confirmButtonColor: "#3085d6",
                                                   })
                                                   .then((result) => {
                                                       if (result.isConfirmed) {
                                                           dispatch(
                                                               getBookletCarfaxRequest({
                                                                   vehicle_id: vehicle?.id || 0,
                                                               })
                                                           );
                                                       }
                                                   })
                                                   .catch((error: any) => {
                                                       console.log("error", error);
                                                   });
                                           }}
                                       >
                                           Fetch Carfax Data
                                       </Button>
                                   </div>
                               ) : null}

                               {!isEdit && index === 4 ? (
                                   <div className="mt-4">
                                       <Button
                                           variant="outline"
                                           color="info"
                                           className="h-max"
                                           onClick={() => {
                                               withReactContent(Swal)
                                                   .fire({
                                                       title: "Generate What Happened",
                                                       text: "Are you sure you want to generate what happened?",
                                                       icon: "warning",
                                                       showCancelButton: true,
                                                       confirmButtonColor: "#3085d6",
                                                   })
                                                   .then((result) => {
                                                       if (result.isConfirmed) {
                                                           dispatch(
                                                               genBookletWhatHappenedRequest({
                                                                   vehicle_id: vehicle?.id || 0,
                                                               })
                                                           );
                                                       }
                                                   })
                                                   .catch((error: any) => {
                                                       console.log("error", error);
                                                   });
                                           }}
                                       >
                                           Generate What Happened
                                       </Button>
                                   </div>
                               ) : null}

                               {!isEdit && index === 5 ? (
                                   <div className="mt-4">
                                       <Button
                                           variant="outline"
                                           color="info"
                                           className="h-max"
                                           onClick={() => {
                                               withReactContent(Swal)
                                                   .fire({
                                                       title: "Generate Overview",
                                                       text: "Are you sure you want to generate overview?",
                                                       icon: "warning",
                                                       showCancelButton: true,
                                                       confirmButtonColor: "#3085d6",
                                                   })
                                                   .then((result) => {
                                                       if (result.isConfirmed) {
                                                           dispatch(
                                                               genBookletWhatWeDidRequest({
                                                                   vehicle_id: vehicle?.id || 0,
                                                               })
                                                           );
                                                       }
                                                   })
                                                   .catch((error: any) => {
                                                       console.log("error", error);
                                                   });
                                           }}
                                       >
                                           Generate Overview
                                       </Button>
                                   </div>
                               ) : null}

                               {!isEdit && index === 7 ? (
                                   <div className="mt-4">
                                       <Button
                                           variant="outline"
                                           color="info"
                                           className="h-max"
                                           onClick={() => {
                                               withReactContent(Swal)
                                                   .fire({
                                                       title: "Generate Factory Base Configuration",
                                                       text: "Are you sure you want to generate factory base configuration?",
                                                       icon: "warning",
                                                       showCancelButton: true,
                                                       confirmButtonColor: "#3085d6",
                                                   })
                                                   .then((result) => {
                                                       if (result.isConfirmed) {
                                                           dispatch(
                                                               genBookletFactoryBaseConfigurationRequest(
                                                                   { vehicle_id: vehicle?.id || 0 }
                                                               )
                                                           );
                                                       }
                                                   })
                                                   .catch((error: any) => {
                                                       console.log("error", error);
                                                   });
                                           }}
                                       >
                                           Generate Factory Base Configuration
                                       </Button>
                                   </div>
                               ) : null}

                               {!isEdit && index === 200 ? (
                                   <React.Fragment>
                                       <div
                                           key={index}
                                           className="flex mt-4 flex-col gap-4 border border-[#E2E8F0] rounded-[8px] px-4 py-14 items-center justify-center relative"
                                       >
                                           <FormFileUpload
                                               name="about_img"
                                               accept=".jpg,.png,.jpeg,.pdf"
                                               title="About Image"
                                               gap="gap-y-3"
                                               desc=""
                                               Icon={AppImages.iconFileClip}
                                               show_preview={true}
                                               className="flex-col"
                                               imgClassName="max-w-full"
                                               wrapperClassName="!static"
                                           />
                                       </div>
                                   </React.Fragment>
                               ) : null}
                           </Card>
                       </div>
                   ))}
               </form>
           </FormProvider>
       </React.Fragment>
   );
}
